import Cookies from "js-cookie";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import MultipleSelector, {
  Option,
} from "@/components/ui/multiple-selector/multiple-selector";

import { usePermissionsModal } from "./use-permission-modal";
import { Button } from "@/components/ui/button/button";
import { useMutation } from "@tanstack/react-query";
import { editEmployeePermissions } from "@/config/mutation/admin";
import { Badge } from "@/components/ui/badge/badge";

interface FormSchemaType {
  permissionType: string;
  grant: {
    permissionType: string;
    data: Option;
  }[];
}

const FormSchema: z.ZodType<FormSchemaType> = z.object({
  permissionType: z.string(),
  grant: z.array(
    z.object({
      permissionType: z.string(),
      data: z.object({
        value: z.string(),
        label: z.string(),
      }),
    })
  ),
});

type FormValues = z.infer<typeof FormSchema>;

const PERMISSION_DOCS: {
  [key: string]: {
    docs: {
      value: string;
      label: string;
    }[];
  };
} = {
  "1": {
    docs: [
      {
        value: "Allowance",
        label: "Allowance",
      },
      {
        value: "LeaveApplication",
        label: "LeaveApplication",
      },
      {
        value: "Loan",
        label: "Loan",
      },
      {
        value: "Salary",
        label: "Salary",
      },
      {
        value: "Leads",
        label: "Leads",
      },
    ],
  },

  "2": {
    docs: [
      {
        value: "Allowance",
        label: "Allowance",
      },
      {
        value: "LeaveApplication",
        label: "LeaveApplication",
      },
      {
        value: "Loan",
        label: "Loan",
      },
      {
        value: "Salary",
        label: "Salary",
      },
      {
        value: "Leads",
        label: "Leads",
      },
    ],
  },

  "3": {
    docs: [
      {
        value: "Allowance",
        label: "Allowance",
      },
      {
        value: "LeaveApplication",
        label: "LeaveApplication",
      },
      {
        value: "Loan",
        label: "Loan",
      },
      {
        value: "Salary",
        label: "Salary",
      },
      {
        value: "Leads",
        label: "Leads",
      },
    ],
  },
};

export const AddPermissionsModal = () => {
  const queryClient = useQueryClient();

  const key = Cookies.get("key") as string;
  const {
    open,
    onOpenChange,
    permissions: alreadyPermissions,
    employeeId,
  } = usePermissionsModal();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      grant: [],
      permissionType: undefined,
    },
  });

  const permissionType = useWatch({
    name: "permissionType",
    control: form.control,
  });

  const grant = useWatch({
    name: "grant",
    control: form.control,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: editEmployeePermissions(key),
  });

  const onSubmit = (data: FormValues) => {
    const grant = data?.grant?.map((item) => ({
      permissionType: item?.permissionType,
      doc: item?.data?.value,
    }));

    // console.log(grant);

    mutate(
      {
        empId: employeeId,
        info: {
          grant,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["employees"],
          });
          toast.success("Permissions Edited");

          onOpenChange(false);

          setTimeout(() => {
            form.reset({
              grant: [],
              permissionType: undefined,
            });
          }, 1000);
        },

        onError: (error) => {
          console.log("ERROR WHILE EDITING PERMISSIONS", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const defaultPermissionsDocs = useMemo(() => {
    if (permissionType === "" || permissionType === undefined) return [];

    let permissionDocs = PERMISSION_DOCS[permissionType]?.docs;

    permissionDocs = permissionDocs?.map((pd) => {
      const doesInclude = alreadyPermissions[permissionType]?.includes(
        pd?.value
      );

      if (!doesInclude) {
        return pd;
      }

      return {
        ...pd,
        disable: true,
      };
    });

    return permissionDocs;
  }, [permissionType, alreadyPermissions]);

  const selectedValues = useMemo(() => {
    if (permissionType === "" || permissionType === undefined) {
      return [];
    }

    const obj = form.getValues("grant");
    const value = obj
      ?.filter((o) => o?.permissionType === permissionType)
      ?.map((t) => t.data);

    return value;
  }, [permissionType]);

  const Component = useMemo(() => {
    return (
      <div className="flex flex-col gap-2">
        <h4 className="text-sm">Permissions</h4>
        <MultipleSelector
          value={selectedValues}
          onChange={(options) => {
            const prev = form.getValues("grant");
            const extracted = prev?.filter(
              (p) => p.permissionType !== permissionType
            );
            const newValue = [
              ...extracted,
              ...options?.map((opt) => ({
                permissionType,
                data: opt,
              })),
            ];

            let freshValues: {
              permissionType: string;
              data: {
                label: string;
                value: string;
              };
            }[] = [];
            newValue.forEach((item) => {
              const findItem = freshValues?.find(
                (t) =>
                  t?.permissionType === item?.permissionType &&
                  t?.data?.value === item?.data?.value
              );

              if (!findItem) {
                freshValues.push(item);
              }
            });

            form.setValue("grant", freshValues);
          }}
          options={defaultPermissionsDocs}
          placeholder="Select permissions..."
          emptyIndicator={
            <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
              no results found.
            </p>
          }
        />

        <div className="mt-5">
          {(() => {
            const grant = form.getValues("grant");
            const readPermissions = grant
              ?.filter((p) => p.permissionType === "1")
              ?.map((p) => p.data?.label);
            const editPermissions = grant
              ?.filter((p) => p.permissionType === "2")
              ?.map((p) => p.data?.label);
            const deleteOrRestorePermissions = grant
              ?.filter((p) => p.permissionType === "3")
              ?.map((p) => p.data?.label);

            return (
              <div className="flex flex-col gap-3">
                {readPermissions?.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <h3>Read Permissions:</h3>
                    <div className="flex flex-wrap items-center gap-1 pl-5">
                      {readPermissions?.map((p) => (
                        <Badge>{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {editPermissions?.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <h3>Edit Permissions:</h3>
                    <div className="flex flex-wrap items-center gap-1 pl-5">
                      {editPermissions?.map((p) => (
                        <Badge>{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {deleteOrRestorePermissions?.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <h3>Delete/Restore Permissions:</h3>
                    <div className="flex flex-wrap items-center gap-1 pl-5">
                      {deleteOrRestorePermissions?.map((p) => (
                        <Badge>{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }, [selectedValues, defaultPermissionsDocs, grant]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add Permissions</DialogTitle>
          </DialogHeader>

          <div className="mt-5">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="permissionType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Permission Type</FormLabel>
                        <Select
                          disabled={isPending}
                          onValueChange={(value) => {
                            field.onChange(value);
                            // setPType(value);
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select permission type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1">Read</SelectItem>
                            <SelectItem value="2">Edit</SelectItem>
                            <SelectItem value="3">Delete/Restore</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {Component}
                </div>

                <div className="flex items-center justify-end mt-3">
                  <Button type="submit" disabled={isPending}>
                    Save
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
